import { client, log, supabase } from ".";
import { agruparClientesPorEmpresa, normalizarEmpresa } from "./empresaContacto";

// Vetos de edificio — Fase 1 (2026-10-07): fuente única de lectura/escritura de `edificios_vetos`
// (reemplaza el CSV legacy `edificios.vetos`, que ya no se lee ni se escribe desde acá).
// Fase 2 (2026-10-07): cada veto puede tener 0..N empresas protegidas (texto normalizado con
// `normalizarEmpresa`, misma clave que `empresas_vip` — el país lo da el edificio, no se repite).
// Sin empresa protegida, el veto bloquea a todas (decisión de Ignacio).

// Lee los vetos vigentes de un edificio: [{ id, categoria_id, empresas_protegidas }, ...].
export const getVetosEdificio = async (edificioId) => {
  try {
    const data = await supabase.select("edificios_vetos", {
      filters: { edificio_id: edificioId },
      orderBy: [{ column: "categoria_id", ascending: true }],
    });
    return data || [];
  } catch (error) {
    log.error("getVetosEdificio", error);
    throw error;
  }
};

// Lee los vetos de VARIOS edificios de una sola vez (evita N+1) — para el detalle "en defensa de
// X" de la tabla admin de Edificios (Fase 2). Devuelve Map(edificio_id -> [{categoria_id,
// empresas_protegidas}]).
export const getVetosPorEdificios = async (edificioIds) => {
  try {
    if (!edificioIds || edificioIds.length === 0) return new Map();
    const { data, error } = await client
      .from("edificios_vetos")
      .select("edificio_id, categoria_id, empresas_protegidas")
      .in("edificio_id", edificioIds);
    if (error) throw error;
    const porEdificio = new Map();
    (data || []).forEach((fila) => {
      const id = Number(fila.edificio_id);
      if (!porEdificio.has(id)) porEdificio.set(id, []);
      porEdificio
        .get(id)
        .push({ categoria_id: fila.categoria_id, empresas_protegidas: fila.empresas_protegidas || [] });
    });
    return porEdificio;
  } catch (error) {
    log.error("getVetosPorEdificios", error);
    throw error;
  }
};

// Reemplaza el set de vetos de un edificio. `vetos` = [{ categoriaId, empresasProtegidas }, ...]
// (empresasProtegidas opcional, default []). Nunca pisa una categoría que no cambió: solo
// INSERTA las categorías nuevas, DELETA las que salieron, y UPDATEa (solo `empresas_protegidas`)
// las que siguen vetadas PERO cambiaron sus empresas protegidas — comparado por SET, no por orden.
// `empresasProtegidas` siempre se normaliza y dedupea antes de guardar (nunca texto libre/crudo).
export const guardarVetosEdificio = async (edificioId, vetos) => {
  try {
    const actuales = await getVetosEdificio(edificioId);
    const actualesPorId = new Map(
      actuales.map((v) => [Number(v.categoria_id), { id: v.id, empresasProtegidas: v.empresas_protegidas || [] }])
    );

    const nuevos = (vetos || []).map((v) => ({
      categoriaId: Number(v.categoriaId),
      empresasProtegidas: [...new Set((v.empresasProtegidas || []).map(normalizarEmpresa).filter(Boolean))].sort(),
    }));
    const nuevosPorId = new Map(nuevos.map((v) => [v.categoriaId, v.empresasProtegidas]));

    const aQuitar = [...actualesPorId.keys()].filter((id) => !nuevosPorId.has(id));
    const aAgregar = nuevos.filter((v) => !actualesPorId.has(v.categoriaId));
    const aActualizar = nuevos.filter((v) => {
      const actual = actualesPorId.get(v.categoriaId);
      if (!actual) return false;
      const antes = JSON.stringify([...actual.empresasProtegidas].sort());
      const despues = JSON.stringify(v.empresasProtegidas);
      return antes !== despues;
    });

    if (aQuitar.length > 0) {
      await supabase.removeWhere(
        "edificios_vetos",
        `edificio_id=eq.${edificioId}&categoria_id=in.(${aQuitar.join(",")})`
      );
    }
    if (aAgregar.length > 0) {
      await supabase.insert(
        "edificios_vetos",
        aAgregar.map((v) => ({
          edificio_id: edificioId,
          categoria_id: v.categoriaId,
          empresas_protegidas: v.empresasProtegidas,
        }))
      );
    }
    await Promise.all(
      aActualizar.map((v) =>
        supabase.update(
          "edificios_vetos",
          { empresas_protegidas: v.empresasProtegidas },
          actualesPorId.get(v.categoriaId).id
        )
      )
    );
    return true;
  } catch (error) {
    log.error("guardarVetosEdificio", error);
    throw error;
  }
};

// Opciones de empresa protegida, por país — Fase 2. Universo: clientes no eliminados del país,
// agrupados por empresa normalizada (misma clave que `empresas_vip`, reusa `normalizarEmpresa`/
// `agruparClientesPorEmpresa` de empresaContacto.js, 10.1/10.27 — no se reimplementa acá).
// Una sola query por país (no por categoría ni por edificio). Devuelve:
//   - porCategoria: Map(categoria_id -> [{empresaNorm, empresaDisplay, cuentas}]) — cuentas DENTRO
//     de esa categoría puntual, ordenado por cuentas desc.
//   - deTodoElPais: [{empresaNorm, empresaDisplay, cuentas}] — TODAS las empresas del país (cuentas
//     = total de la empresa, cualquier categoría), ordenado alfabético, para el buscador.
export const getOpcionesEmpresasPorPais = async (pais) => {
  try {
    const data = await supabase.select("clientes", { filters: { pais } });
    const activos = (data || []).filter((c) => !c.eliminada && c.empresa && c.empresa.trim());

    const aOpciones = (lista) =>
      [...agruparClientesPorEmpresa(lista).values()].map((g) => ({
        empresaNorm: g.empresaNorm,
        empresaDisplay: g.empresaDisplay,
        cuentas: g.clienteIds.length,
      }));

    const porCategoriaBuckets = new Map();
    activos.forEach((c) => {
      if (c.categoria === null || c.categoria === undefined) return;
      const catId = Number(c.categoria);
      if (!porCategoriaBuckets.has(catId)) porCategoriaBuckets.set(catId, []);
      porCategoriaBuckets.get(catId).push(c);
    });

    const porCategoria = new Map();
    porCategoriaBuckets.forEach((lista, catId) => {
      porCategoria.set(
        catId,
        aOpciones(lista).sort((a, b) => b.cuentas - a.cuentas || a.empresaDisplay.localeCompare(b.empresaDisplay))
      );
    });

    const deTodoElPais = aOpciones(activos).sort((a, b) => a.empresaDisplay.localeCompare(b.empresaDisplay));

    return { porCategoria, deTodoElPais };
  } catch (error) {
    log.error("getOpcionesEmpresasPorPais", error);
    throw error;
  }
};
